/**
 * Classifieds — 2-column marketplace grid with photo placeholder, category
 * tag, price, listing flat. Wired: GET /api/discussions/classifieds.
 * Port of ClassifiedsScreen in design/source/screens-vendor-issue.jsx.
 */
import React from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { CatPill, goBack } from '@/features/community/bits';
import { fetchClassifieds, useFetch, type Classified } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Screen, Skeleton, TopBar } from '@/ui';

export default function ClassifiedsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchClassifieds, []);
  const list = data ?? [];

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ml_classifieds')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s3 }}>
            {[0, 1, 2, 3].map((i) => (
              <View key={i} style={{ width: '47%' }}>
                <Skeleton h={110} r={12} />
                <Skeleton w="70%" h={12} style={{ marginTop: space.s2 }} />
                <Skeleton w="40%" h={16} style={{ marginTop: 6 }} />
              </View>
            ))}
          </View>
        ) : error ? (
          <EmptyState
            title={t('cl_err_title')}
            body={t('cl_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="package" size={40} color={colors.textTertiary} />}
            title={t('cl_empty_title')}
            body={t('cl_empty_body')}
          />
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s3 }}>
            {list.map((it, i) => <ClassifiedCard key={it.id} item={it} index={i} />)}
          </View>
        )}
      </Screen>
    </View>
  );
}

/** Tone rotation for the item tile — the design colors these tiles per item;
 * amber is capped to exactly one card (index 1) to honor the "accent exactly
 * once per screen" brand rule instead of the mock's freer repetition. */
function classifiedTone(i: number): 'primary' | 'accent' | 'neutral' {
  if (i === 1) return 'accent';
  return i % 2 === 0 ? 'primary' : 'neutral';
}

function ClassifiedTile({ tone }: { tone: 'primary' | 'accent' | 'neutral' }) {
  const { colors } = useTheme();
  const map = {
    primary: { bg: colors.primary100, chip: colors.primary400 },
    accent: { bg: colors.accent100, chip: colors.accent500 },
    neutral: { bg: colors.canvas2, chip: colors.borderFirm },
  } as const;
  const { bg, chip } = map[tone];
  return (
    <View style={{ height: 110, backgroundColor: bg, alignItems: 'center', justifyContent: 'center' }}>
      <View style={{
        width: 44, height: 50, opacity: 0.7, backgroundColor: chip,
        borderTopLeftRadius: 22, borderTopRightRadius: 22, borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
      }} />
    </View>
  );
}

function ClassifiedCard({ item, index }: { item: Classified; index: number }) {
  return (
    <View style={{ width: '47%', overflow: 'hidden', borderRadius: radius.lg }}>
      <ClassifiedTile tone={classifiedTone(index)} />
      <View style={{ padding: space.s3 }}>
        <CatPill label={item.tag} />
        <AppText variant="bodyMd" weight="500" numberOfLines={1} style={{ marginTop: 6 }}>{item.title}</AppText>
        {item.price ? (
          <AppText variant="headingSm" weight="600" mono style={{ marginTop: 4 }}>{item.price}</AppText>
        ) : null}
        {item.by ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{item.by}</AppText> : null}
      </View>
    </View>
  );
}
